import React, { useState, useEffect, useCallback } from 'react';
import { Form, Button, Input, Row, Col, message, Switch, Card, Alert, Divider, Select } from 'antd';
import { PayCircleOutlined, SaveOutlined, EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import { ROW_GUTTER } from 'constants/ThemeConstant';
import { motion } from "framer-motion";
import apiClient from 'services/apiClient';
import { useMyContext } from 'Context/MyContextProvider';

const { Option } = Select;

const EasebuzzGateway = ({ api, authToken, navigate }) => {
    const { Permisson } = useMyContext();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [isEnabled, setIsEnabled] = useState(false);
    const [merchantKey, setMerchantKey] = useState('');
    const [salt, setSalt] = useState('');
    const [environment, setEnvironment] = useState('test');
    const [merchantId, setMerchantId] = useState('');

   

    const fetchGatewaySettings = useCallback(async () => {
        setLoading(true);
        try {
            const response = await apiClient.get(`${api}payment-gateway/easebuzz`, {
                headers: {
                    'Authorization': 'Bearer ' + authToken,
                }
            });
            const data = response.data;
            
            // Handle the response according to your table structure with credentials object
            setIsEnabled(data.isEnabled || data.status === 'active' || false);
            
            // Extract from credentials object
            const credentials = data.credentials || {};
            setMerchantKey(credentials.merchantKey || '');
            setSalt(credentials.salt || '');
            setMerchantId(credentials.merchantId || '');
            setEnvironment(data.environment || 'test');
            
            form.setFieldsValue({
                merchant_key: credentials.merchantKey || '',
                salt: credentials.salt || '',
                merchant_id: credentials.merchantId || '',
                environment: data.environment || 'test',
            });
        } catch (error) {
            console.error('Error fetching Easebuzz settings:', error);
            // If no settings found, set default values
            form.setFieldsValue({
                environment: 'test'
            });
        } finally {
            setLoading(false);
        }
    }, [api, authToken, form]);

    useEffect(() => {
        fetchGatewaySettings();
    }, [fetchGatewaySettings]);

    const onFinish = async (values) => {
        setLoading(true);
        try {
            const payload = {
                gateway: 'easebuzz',
                credentials: {
                    merchantKey: values.merchant_key,
                    salt: values.salt,
                    merchantId: values.merchant_id
                },
                environment: values.environment, // 'test' or 'production'
                isEnabled: isEnabled,
                status: isEnabled ? 'active' : 'inactive'
            };

            const response = await apiClient.post(`${api}payment-gateway/easebuzz`, payload, {
                headers: {
                    'Authorization': 'Bearer ' + authToken,
                    'Content-Type': 'application/json'
                }
            });

            if (response.data.success) {
                message.success('Easebuzz settings updated successfully!');
                // Refresh the data after successful update
                fetchGatewaySettings();
            } else {
                message.error(response.data.message || 'Failed to update Easebuzz settings');
            }
        } catch (error) {
            console.error('Error updating Easebuzz settings:', error);
            message.error('Error updating Easebuzz settings');
        } finally {
            setLoading(false);
        }
    };

    const handleSwitchChange = (checked) => {
        setIsEnabled(checked);
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
        >
            <Card bordered={false} className='shadow-none'>
                <div className="d-flex align-items-center mb-4">
                    <div>
                        <h2 className="mb-0">Easebuzz Gateway</h2>
                        <p className="text-muted mb-0">Configure your Easebuzz payment gateway settings</p>
                    </div>
                </div>

                <Alert
                    message="Important"
                    description="Easebuzz provides comprehensive payment solutions. Ensure you have the correct merchant credentials from your Easebuzz dashboard."
                    type="info"
                    showIcon
                    className="mb-4"
                />

                <Row gutter={ROW_GUTTER}>
                    <Col xs={24} sm={24} md={17}>
                        <Card className="border border-light rounded-3">
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <h5 className="mb-0">Gateway Status</h5>
                                <Switch
                                    checked={isEnabled}
                                    onChange={handleSwitchChange}
                                    checkedChildren="Enabled"
                                    unCheckedChildren="Disabled"
                                />
                            </div>

                            <Divider />

                            <Form
                                form={form}
                                layout="vertical"
                                onFinish={onFinish}
                                className="mt-4"
                            >
                                <Row gutter={ROW_GUTTER}>
                                    <Col xs={24} sm={24} md={12}>
                                        <Form.Item
                                            label="Environment"
                                            name="environment"
                                            rules={[
                                                { required: true, message: 'Please select environment' }
                                            ]}
                                        >
                                            <Select
                                                placeholder="Select environment"
                                                value={environment}
                                                onChange={(value) => setEnvironment(value)}
                                                className="rounded-2"
                                            >
                                                <Option value="test">Test Mode</Option>
                                                <Option value="prod">Production Mode</Option>
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={24} md={12}>
                                        <Form.Item
                                            label="Merchant ID"
                                            name="merchant_id"
                                            rules={[
                                                { required: true, message: 'Please enter your Merchant ID' }
                                            ]}
                                        >
                                            <Input
                                                placeholder="Enter Merchant ID"
                                                value={merchantId}
                                                onChange={(e) => setMerchantId(e.target.value)}
                                                className="rounded-2"
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Row gutter={ROW_GUTTER}>
                                    <Col xs={24} sm={24} md={12}>
                                        <Form.Item
                                            label="Merchant Key"
                                            name="merchant_key"
                                            rules={[
                                                { required: true, message: 'Please enter your Merchant Key' }
                                            ]}
                                        >
                                            <Input.Password
                                                placeholder="Enter Merchant Key"
                                                value={merchantKey}
                                                onChange={(e) => setMerchantKey(e.target.value)}
                                                iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                                                className="rounded-2"
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={24} md={12}>
                                        <Form.Item
                                            label="Salt"
                                            name="salt"
                                            rules={[
                                                { required: true, message: 'Please enter your Salt' }
                                            ]}
                                        >
                                            <Input.Password
                                                placeholder="Enter Salt"
                                                value={salt}
                                                onChange={(e) => setSalt(e.target.value)}
                                                iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                                                className="rounded-2"
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Form.Item className="mt-2">
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        loading={loading}
                                        className='float-right'
                                        icon={<SaveOutlined />}
                                    >
                                        Save Easebuzz Settings
                                    </Button>
                                </Form.Item>
                            </Form>
                        </Card>
                    </Col>

                    <Col xs={24} sm={24} md={7}>
                        <Card className="border border-light rounded-3 bg-light">
                            <h6 className="fw-bold mb-3">Setup Instructions</h6>
                            <div className="small text-muted">
                                <ol className="ps-3">
                                    <li className="mb-2">
                                        Log in to your <a href="https://merchant.easebuzz.in" target="_blank" rel="noopener noreferrer">Easebuzz Merchant Dashboard</a>
                                    </li>
                                    <li className="mb-2">
                                        Navigate to Developer → API Keys section
                                    </li>
                                    <li className="mb-2">
                                        Copy your Merchant ID, Key, and Salt
                                    </li>
                                    <li className="mb-2">
                                        Configure webhook URLs for transaction status
                                    </li>
                                    <li className="mb-2">
                                        Test integration in test mode first
                                    </li>
                                    <li className="mb-0">
                                        Switch to production when testing is complete
                                    </li>
                                </ol>
                            </div>

                            <Alert
                                message="Test Credentials"
                                description="Use test credentials provided by Easebuzz for development and testing purposes."
                                type="warning"
                                showIcon
                                className="mt-3"
                                size="small"
                            />
                        </Card>
                    </Col>
                </Row>
            </Card>
        </motion.div>
    );
};

export default EasebuzzGateway;
